/** Sieve — English strings (§11). ~60 strings incl. 15 outcome messages. */

import type { Dict } from './types';

export const en: Dict = {
  meta: { name: 'English', dir: 'ltr' },
  brand: 'Sieve',
  tagline: 'Find the 5 primes.',

  header: { menu: 'Menu', language: 'Language', stats: 'Statistics', help: 'How to play' },

  play: {
    puzzleLabel: 'Sieve No. {id}',
    primesLeft: 'Primes',
    mistakes: 'Mistakes',
    loading: 'Loading today’s puzzle…',
    offline: 'You’re offline — playing from the cached queue.',
  },

  outcome: {
    flawless: 'Flawless',
    solved: 'Solved',
    failed: 'Out of guesses',
  },

  // 5 lines per tier (§3.2). Dry, short, never condescending.
  messages: {
    flawless: [
      'Not one wasted tap.',
      'Clean sweep.',
      'You saw straight through it.',
      'Nothing got past you.',
      'Textbook sieving.',
    ],
    solved: [
      'Sifted out in the end.',
      'A couple of near-misses. Solved all the same.',
      'The survivors gave you trouble. You won anyway.',
      'Got there.',
      'Composite dodged, primes found.',
    ],
    failed: [
      'The grid kept a few secrets today.',
      'Three traps sprung. Tomorrow’s another sieve.',
      'Close doesn’t clear the grid.',
      'Some composites wear a good disguise.',
      'Back tomorrow — the 7s are waiting.',
    ],
  },

  cell: { composite: 'composite', prime: 'prime' },

  mult: {
    banner: 'Divisible by {x}',
    findAll: 'find all 5',
    found: 'Found',
    divisible: 'divisible by {x}',
    notDivisible: 'not divisible by {x}',
    remainder: 'remainder {r}',
    menuToday: 'Today — Multiples',
    primeToday: 'Today — Sieve · primes',
    harder: 'harder',
    onboard1Title: 'Find the multiples',
    onboard1Body: 'You’ll be told a number — like 7. Tap the 5 numbers divisible by it. 3 mistakes allowed.',
    onboard2Title: 'Shortcuts',
    onboard2Body: '÷2 ends even · ÷5 ends in 0 or 5 · ÷3 the digits sum to a multiple of 3. For the rest, divide it out.',
    messages: {
      flawless: [
        'Every multiple, no misfires.',
        'You had its number.',
        'Clean division.',
        'Not one remainder fooled you.',
        'Straight through the sieve.',
      ],
      solved: [
        'Found them all in the end.',
        'A near-miss or two. Still solved.',
        'The distractors nearly had you.',
        'Divided and conquered.',
        'Got every last one.',
      ],
      failed: [
        'A few too many remainders today.',
        'The near-misses did their job.',
        'So close to a clean sweep.',
        'Some of those really looked divisible.',
        'Back tomorrow — a new divisor awaits.',
      ],
    },
  },

  end: {
    nextIn: 'Next puzzle in',
    share: 'Share',
    copied: 'Copied to clipboard',
    shared: 'Shared',
    shareFailed: 'Couldn’t share',
    archive: 'Play past puzzles',
    keepGoing: 'Keep going → Rapid',
    yourResult: 'Your result',
  },

  stats: {
    title: 'Statistics',
    played: 'Played',
    winPct: 'Win %',
    currentStreak: 'Current streak',
    maxStreak: 'Max streak',
    flawless: 'Flawless',
    flawlessPct: 'Flawless %',
    distribution: 'Mistake distribution',
    rapidBest: 'Rapid best',
    buckets: { '0': '0', '1': '1', '2': '2', failed: 'Fail' },
    none: 'No games yet. Play today’s puzzle.',
  },

  help: {
    title: 'How to play',
    card1Title: 'Find the 5 primes',
    card1Body: 'Twenty-five three-digit numbers. Exactly five are prime. Tap the primes. Three mistakes allowed.',
    card2Title: 'Quick eliminations',
    card2Body: 'Even → out. Ends in 5 → out. Digits sum to a multiple of 3 → out.',
    card3Title: 'Then the traps',
    card3Body: 'What’s left needs checking against 7, 11, 13, and up to 31. That’s the whole game.',
    cheatTitle: 'Divisibility cheat sheet',
    cheat: [
      '2 — last digit is even',
      '3 — digits sum to a multiple of 3',
      '5 — ends in 0 or 5',
      '7 — no shortcut; divide it out',
      '11 — alternating digit sum is 0 or ±11',
      '13 — no shortcut; divide it out',
      '17–31 — the stubborn ones; check each',
    ],
    skip: 'Skip',
    start: 'Start playing',
    next: 'Next',
  },

  settings: {
    title: 'Settings',
    highContrast: 'High contrast',
    colorBlind: 'Colour-blind-safe palette',
    language: 'Language',
    auto: 'Automatic',
  },

  rapid: {
    title: 'Rapid',
    subtitle: 'One number at a time. How far can you get?',
    prime: 'Prime',
    notPrime: 'Not prime',
    streak: 'Streak',
    best: 'Best',
    gameOver: 'Run over',
    was: 'was',
    playAgain: 'Play again',
    back: 'Back to daily',
    start: 'Start',
  },

  menu: {
    title: 'Menu',
    daily: 'Today’s puzzle',
    rapid: 'Rapid mode',
    archive: 'Archive',
    stats: 'Statistics',
    settings: 'Settings',
    help: 'How to play',
    privacy: 'Privacy',
    terms: 'Terms',
    attributions: 'Attributions',
  },

  archive: {
    title: 'Archive',
    subtitle: 'Past puzzles. These don’t affect your streak.',
    play: 'Play',
    done: 'Done',
    locked: 'Not yet available',
    back: 'Back',
  },

  common: { close: 'Close', on: 'On', off: 'Off' },
};
